import { defaultAllSelection } from "services/constants/selections";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import * as Mui from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { showToast } from "services/utils/Status";
import moment from "moment";
import { Loader } from "components/Loader";
import sessionHandling from "services/utils/notificationUtils";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import { themes, optionalAvathar, theme } from "services/constants";
import { RemoveRedEye, InfoOutlined } from "@mui/icons-material";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import TripDetailScreen from "./TripDetails";
import BSDateRangePicker from "pages/MyZone/Status/DateRangePicker";
import { tooltipStyleProps } from "services/constants/Styles";
import { FilterProvider } from "pages/FilterContext";
import { useJsApiLoader } from "@react-google-maps/api";
import { tableRowSize } from "services/constants";
import { WorkSessionsHeaders } from "../TrackingHeaders";
import { Typography, Box, Paper } from "@mui/material";
import { stickyHeader } from "services/constants";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { ThemeProvider } from "@mui/material/styles";
import { setFilterApi } from "services/Redux/userManagament";
import { useSaveView } from "components/CustomComponents/useSaveView";

const WorkSessions = () => {
  const { domain, token, userDetails, topbarCompanyId, userScreenAccess } =
    useSelector((state) => state.tokenReducer);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showMap, setShowMap] = useState(false);
  const [overallTripDetails, setOverallTripDetails] = useState([]);
  const [data, setData] = useState([]);
  const [currentTrip, setCurrentTrip] = useState("");
  const [startDate, setStartDate] = useState(moment().format("DD/MM/YYYY"));
  const [endDate, setEndDate] = useState(moment().format("DD/MM/YYYY"));
  const [selUser, setSeluser] = useState("All Users");
  const [selUserId, setSelUserId] = useState(String(defaultAllSelection));
  const [allUsers, setAllUsers] = useState([]);
  const [showAutoCompleteAvathar, setShowAutoCompleteAvathar] = useState(false);
  const containerRef = useRef(null);
  const dispatch = useDispatch();
  const filterHeaders = useMemo(
    () => WorkSessionsHeaders.filter((item) => item.name !== "View Trip"),
    [],
  );

  const { isLoaded: isMapLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyDDC3xzU7WkPldGiqEkB6LkZyXrGsI_coA",
    id: "google-map-script",
    libraries: ["maps", "geometry"],
  });

  const shiftModule = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Work Sessions",
  );

  const subModuleId = shiftModule?.submodules?.find(
    (sub) => sub.sub_module_name === "Work Sessions",
  )?.sub_module_id;

  const fetchTrips = useCallback(async () => {
    try {
      setIsLoading(true);

      const sDate = startDate.split("/").reverse().join("-");
      const eDate = endDate.split("/").reverse().join("-");

      const tripAPI =
        selUserId !== String(defaultAllSelection)
          ? `${domain}tracking/trip-details/?start_date=${sDate}&end_date=${eDate}&selected_user_id=${selUserId}&company_id=${topbarCompanyId}`
          : `${domain}tracking/trip-details/?start_date=${sDate}&end_date=${eDate}&company_id=${topbarCompanyId}`;

      const response = await fetch(tripAPI, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
      });

      const res = await response.json();

      if (response.ok) {
        setOverallTripDetails(res || []);
        setData(res || []);
        dispatch(setFilterApi(true));
      } else if (response.status === 409) {
        setOverallTripDetails([]);
        setData([]);
        sessionHandling();
      } else {
        setOverallTripDetails([]);
        setData([]);
      }
    } catch (error) {
      setOverallTripDetails([]);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    domain,
    token,
    startDate,
    endDate,
    topbarCompanyId,
    selUserId,
    dispatch,
  ]);

  useEffect(() => {
    fetchTrips();
  }, [fetchTrips, userDetails]);

  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const response = await fetch(
          `${domain}list/employee?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
          {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
          },
        );
        const res = await response.json();
        if (response.ok) {
          const AllUsersData = [
            {
              id: String(defaultAllSelection),
              username: "All Users",
              label: "All Users",
            },
            // list/employee identifies people by user_id and names by user_name.
            ...(Array.isArray(res) ? res : []).map((obj) => ({
              ...obj,
              id: obj.user_id ?? obj.id,
              username: obj.user_name || obj.username,
              label: obj.user_name || obj.username,
            })),
          ];
          setAllUsers(AllUsersData);
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error('REQUEST_FAILED');
        }
      } catch (error) {
        console.error('[Index] operation failed:', error);
        showToast("error", "Couldn't load work session details. Please refresh the page.");
      }
    };
    fetchAllUsers();
  }, [domain, token, userDetails?.id, topbarCompanyId]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const updateUserName = (event, data) => {
    if (data) {
      setSelUserId(data?.id);
      setSeluser(data);
      setShowAutoCompleteAvathar(false);
    } else {
      setShowAutoCompleteAvathar(true);
    }
  };

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setOverallTripDetails(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const viewTripHeader = useMemo(
    () => WorkSessionsHeaders.find((header) => header.name === "View Trip"),
    [],
  );

  const { visibleHeaders, titleOverride, newButtonComponent } = useSaveView({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "operations_work_sessions_saved_views",
    fullHeaders: filterHeaders,
    screenAccess: true,
    onViewChange: handleSavedViewChange,
  });

  const tableHeaders = useMemo(() => {
    if (!viewTripHeader) return visibleHeaders;
    return visibleHeaders.some((header) => header.name === "View Trip")
      ? visibleHeaders
      : [...visibleHeaders, viewTripHeader];
  }, [viewTripHeader, visibleHeaders]);

  return (
    <React.Fragment>
      {!showMap ? (
        <>
          {isLoading ? (
            <Loader />
          ) : (
            <ThemeProvider theme={theme}>
              <FilterProvider
                domain={domain}
                userDetails={userDetails}
                subModuleId={subModuleId}
              >
                <Box
                  sx={{
                    flexGrow: 1,
                    p: 3,
                    minWidth: 0,
                    backgroundColor: "#f5f5fd",
                    transition: "all 0.3s ease",
                  }}
                >
                  <Mui.Breadcrumbs
                    aria-label="breadcrumb"
                    separator="›"
                    sx={{ mb: 2 }}
                  >
                    <Typography>Operations</Typography>
                    <Typography color={themes?.blueColor}>
                      Work Sessions
                    </Typography>
                  </Mui.Breadcrumbs>
                  <Box sx={{ mb: 2 }}>
                    <Mui.Grid container alignItems="center" spacing={2}>
                      <Mui.Grid item xs={12} md={5}>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                          Work Sessions
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          View team's live trip route, key details, and
                          real-time travel updates on the map.
                        </Typography>
                      </Mui.Grid>

                      <Mui.Grid item xs={12} md={7}>
                        <Mui.Stack
                          direction="row"
                          spacing={2}
                          justifyContent="flex-end"
                        >
                          <Mui.FormControl sx={{ minWidth: 270 }}>
                            <BSDateRangePicker
                              fromDate={startDate}
                              toDate={endDate}
                              handleStartDate={handleDateChange}
                              showToday
                              includeCurrentDate
                            />
                          </Mui.FormControl>

                          <Box sx={{ minWidth: 220 }}>
                            <CustomAutoCompleteWithLogo
                              dataList={allUsers?.map((obj) => ({
                                ...obj,
                                label: obj?.user_name || obj?.username,
                                image: obj?.image_url,
                              }))}
                              selectedValue={selUser}
                              updateDataFunction={updateUserName}
                              placeHolder="Select user name"
                              label="Users"
                              showAvathar={showAutoCompleteAvathar}
                              setShowAutoCompleteAvathar={
                                setShowAutoCompleteAvathar
                              }
                            />
                          </Box>
                        </Mui.Stack>
                      </Mui.Grid>
                    </Mui.Grid>
                  </Box>

                  <Paper sx={{ width: "100%", height: "77vh" }}>
                    <CustomFilter
                      title="Work Sessions"
                      titleOverride={titleOverride}
                      filterHeaders={visibleHeaders}
                      data={data}
                      filteredData={overallTripDetails}
                      onFilteredDataChange={handleFilteredDataChange}
                      screenAccess={true}
                      newButtonComponent={newButtonComponent}
                    />

                    <Mui.Grid sx={{ ml: 2, mr: 2 }}>
                      <Mui.TableContainer
                        ref={containerRef}
                        sx={{ maxHeight: 590 }}
                      >
                        <Mui.Table className="border">
                          <Mui.TableHead sx={stickyHeader}>
                            <Mui.TableRow sx={tableRowSize}>
                              {tableHeaders?.map((value) => (
                                <Mui.TableCell
                                  key={value?.name}
                                  sx={{
                                    color: themes.headLine,
                                    fontWeight: "bold",
                                  }}
                                >
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={0.5}
                                  >
                                    {value?.name}

                                    {value?.name === "Distance" && (
                                      <Mui.Tooltip
                                        arrow
                                        slotProps={tooltipStyleProps}
                                        title="Distance shown is an estimate. Accuracy may vary due to GPS signal strength, device movement, or network conditions."
                                      >
                                        <Mui.IconButton size="small">
                                          <InfoOutlined fontSize="small" />
                                        </Mui.IconButton>
                                      </Mui.Tooltip>
                                    )}
                                  </Box>
                                </Mui.TableCell>
                              ))}
                            </Mui.TableRow>
                          </Mui.TableHead>
                          <Mui.TableBody>
                            {overallTripDetails?.length === 0 ? (
                              <TableEmptyState dataLength={data} />
                            ) : (
                              overallTripDetails
                                ?.slice(
                                  page * rowsPerPage,
                                  page * rowsPerPage + rowsPerPage,
                                )
                                ?.map((value) => (
                                  <Mui.TableRow
                                    key={`${value?.trip_id ?? "trip"}_${value?.user_id ?? ""}_${value?.date ?? ""}`}
                                    sx={tableRowSize}
                                  >
                                    {tableHeaders.map((head) => (
                                      <Mui.TableCell key={head.key || head.name}>
                                        {head?.name === "Trip Name" ? (
                                          `Trip - ${value?.trip_id}`
                                        ) : head?.name === "Employee Name" ? (
                                          <Mui.Tooltip
                                            arrow
                                            slotProps={tooltipStyleProps}
                                            title={value?.user_name}
                                            disableHoverListener={
                                              !value?.user_name
                                            }
                                          >
                                            <Mui.Stack
                                              direction="row"
                                              spacing={1.5}
                                              alignItems="center"
                                            >
                                              <Mui.Avatar
                                                sx={{
                                                  width: 23,
                                                  height: 23,
                                                }}
                                                alt={value?.user_name
                                                  ?.charAt(0)
                                                  ?.toUpperCase()}
                                                src={
                                                  value?.image_url ??
                                                  optionalAvathar
                                                }
                                              />
                                              &nbsp; &nbsp;
                                              {value?.user_name}
                                            </Mui.Stack>
                                          </Mui.Tooltip>
                                        ) : head.name === "View Trip" ? (
                                          <RemoveRedEye
                                            color="action"
                                            onClick={() => {
                                              setCurrentTrip(value);
                                              setShowMap(true);
                                            }}
                                          />
                                        ) : (
                                          (value[head.key] ?? "N/A")
                                        )}
                                      </Mui.TableCell>
                                    ))}
                                  </Mui.TableRow>
                                ))
                            )}
                          </Mui.TableBody>
                        </Mui.Table>
                      </Mui.TableContainer>
                      <Mui.Box sx={{ mt: 1 }}>
                        {overallTripDetails?.length > 25 && (
                          <Mui.TablePagination
                            className="custom-pagination"
                            component="div"
                            rowsPerPageOptions={[25, 50, 75, 100]}
                            count={overallTripDetails?.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                          />
                        )}
                      </Mui.Box>
                    </Mui.Grid>
                  </Paper>
                </Box>
              </FilterProvider>
            </ThemeProvider>
          )}
        </>
      ) : (
        <FilterProvider
          domain={domain}
          userDetails={userDetails}
          subModuleId={subModuleId}
        >
          <TripDetailScreen
            setShowMap={setShowMap}
            fetchTrips={fetchTrips}
            currentTrip={currentTrip}
            isLoaded={isMapLoaded}
          />
        </FilterProvider>
      )}
    </React.Fragment>
  );
};

export default WorkSessions;
